'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { mobileUserFacingErrorMessage } from '@/shared/mobile-service/api/cors-mobile-api';
import type { MessageThread } from '@/shared/mobile-service/lib/message-mapping';
import { EmptyState, ErrorState, LoadingState } from '../ui';

/** Left pane: selectable list of conversation threads. */
export function ThreadList({
  threads,
  selectedKey,
  isLoading,
  error,
  onRetry,
  onSelect,
}: {
  threads: MessageThread[];
  selectedKey: string | null;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  onSelect: (key: string) => void;
}) {
  const t = useTimePilotT();
  if (isLoading) return <LoadingState label={t("viewers.loadingConversations")} />;
  if (error) return <ErrorState message={mobileUserFacingErrorMessage(error)} onRetry={onRetry} />;
  if (threads.length === 0) return <EmptyState message={t("viewers.noConversationsFound")} />;

  return (
    <ul className="divide-y divide-white/5">
      {threads.map((thread) => {
        const selected = thread.key === selectedKey;
        return (
          <li key={thread.key}>
            <button
              type="button"
              onClick={() => onSelect(thread.key)}
              className={`flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left transition hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-blue-light ${
                selected ? 'bg-brand-blue/15' : ''
              }`}
            >
              <span className="flex w-full items-baseline justify-between gap-2">
                <span className="truncate text-sm font-medium text-white">{thread.title}</span>
                {typeof thread.count === 'number' && (
                  <span
                    className="shrink-0 text-xs tabular-nums text-white/50"
                    aria-label={`${thread.count.toLocaleString()} messages`}
                    title={`${thread.count.toLocaleString()} messages`}
                  >
                    {thread.count.toLocaleString()}
                  </span>
                )}
              </span>
              {thread.subtitle && (
                <span className="truncate text-xs text-white/65">{thread.subtitle}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
